import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  CircularProgress,
} from '@mui/material';
import { colors } from '../theme/modernTheme';

export default function CancelReminderDialog({ open, onClose, onConfirm, reminder }) {
  const [cancelOption, setCancelOption] = useState('cancel_only');
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const pausePledge = cancelOption === 'pause_pledge';
      await onConfirm(reminder.id, pausePledge);
      onClose();
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setCancelOption('cancel_only');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '4px',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: '1.25rem',
          fontWeight: 600,
          color: colors.text.primary,
          pb: 1,
        }}
      >
        Cancel Reminder
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body2"
          sx={{
            color: colors.text.secondary,
            mb: 3,
          }}
        >
          Choose how you want to cancel this reminder:
        </Typography>
        <RadioGroup value={cancelOption} onChange={(e) => setCancelOption(e.target.value)}>
          <FormControlLabel
            value="cancel_only"
            control={
              <Radio
                sx={{
                  color: colors.text.secondary,
                  '&.Mui-checked': {
                    color: colors.primary.main,
                  },
                }}
              />
            }
            label={
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    color: colors.text.primary,
                  }}
                >
                  Cancel only this reminder
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.text.secondary,
                    fontSize: '0.875rem',
                  }}
                >
                  The pledge will remain active and future reminders will continue
                </Typography>
              </Box>
            }
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            value="pause_pledge"
            control={
              <Radio
                sx={{
                  color: colors.text.secondary,
                  '&.Mui-checked': {
                    color: colors.primary.main,
                  },
                }}
              />
            }
            label={
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    color: colors.text.primary,
                  }}
                >
                  Pause entire pledge
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.text.secondary,
                    fontSize: '0.875rem',
                  }}
                >
                  The pledge will be paused and no future reminders will be generated
                </Typography>
              </Box>
            }
          />
        </RadioGroup>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 2,
        }}
      >
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{
            borderRadius: '4px',
            color: colors.text.secondary,
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={loading}
          variant="contained"
          sx={{
            backgroundColor: colors.error.main,
            borderRadius: '4px',
            color: '#fff',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: colors.error.dark,
            },
            '&:disabled': {
              backgroundColor: colors.action.disabled,
            },
          }}
        >
          {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
